import { useRef, useEffect, useCallback } from 'react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { plainTextToHtml } from '../utils/plainTextToHtml';

const DEBOUNCE_MS = 300;

/** Tiptap emits `<p></p>` when the editor is empty — normalize to empty string. */
const EMPTY_DOC_RE = /^(<p><\/p>)+$/;

interface UseRichTextEditorOptions {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  onFocus?: () => void;
  /** Fires after any pending debounced `onChange` has been flushed, so the
   *  caller sees the committed text (AP-773 A7: one step per blur). */
  onBlur?: () => void;
}

export function useRichTextEditor({
  value,
  onChange,
  disabled = false,
  onFocus,
  onBlur,
}: UseRichTextEditorOptions) {
  const onChangeRef = useRef(onChange);
  const onFocusRef = useRef(onFocus);
  const onBlurRef = useRef(onBlur);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Tracks whether the latest value change originated from the editor itself. */
  const isInternalUpdate = useRef(false);

  useEffect(() => {
    onChangeRef.current = onChange;
    onFocusRef.current = onFocus;
    onBlurRef.current = onBlur;
  }, [onChange, onFocus, onBlur]);

  const emit = useCallback((editor: { getHTML: () => string }) => {
    isInternalUpdate.current = true;
    const html = editor.getHTML();
    onChangeRef.current(EMPTY_DOC_RE.test(html) ? '' : html);
  }, []);

  const handleUpdate = useCallback(
    ({ editor }: { editor: { getHTML: () => string } }) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        debounceRef.current = null;
        emit(editor);
      }, DEBOUNCE_MS);
    },
    [emit]
  );

  const handleFocus = useCallback(() => {
    onFocusRef.current?.();
  }, []);

  const handleBlur = useCallback(
    ({ editor }: { editor: { getHTML: () => string } }) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
        emit(editor);
      }
      onBlurRef.current?.();
    },
    [emit]
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        code: false,
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
      }),
    ],
    content: plainTextToHtml(value),
    editable: !disabled,
    onUpdate: handleUpdate,
    onFocus: handleFocus,
    onBlur: handleBlur,
  });

  // Sync editable state when disabled prop changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  // Sync external value changes (e.g. API load) without resetting cursor.
  // Skip when the change originated from the editor's own onUpdate (user typing).
  useEffect(() => {
    if (!editor) return;

    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }

    const currentHtml = editor.getHTML();
    const normalizedCurrent = EMPTY_DOC_RE.test(currentHtml) ? '' : currentHtml;
    const incomingHtml = plainTextToHtml(value);
    const normalizedIncoming =
      !incomingHtml || EMPTY_DOC_RE.test(incomingHtml) ? '' : incomingHtml;

    if (normalizedCurrent !== normalizedIncoming) {
      editor.commands.setContent(incomingHtml || '', { emitUpdate: false });
    }
  }, [editor, value]);

  // Cleanup debounce timer on unmount
  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  return editor;
}
